import { Mesh, Scalar, TransformNode, Vector2, Vector3 } from '@babylonjs/core';
import { World } from 'miniplex';

import type { Entity } from '../';

/**
 * Unity와 Babylon의 좌표계가 달라 Unity에서 추출한 Vector 값을 Babylon 좌표계에 알맞는 Vector 값으로 변환해 반환합니다.
 * @param vector Unity Vector
 * @returns Babylon Vector
 */
export const convertUnityCoordsToBabylon = (vector: Vector3) => {
  return new Vector3(-vector.x, vector.y, vector.z);
};
export const convertUnityRotationYToBabylon = (value: number) => {
  return -value;
};
export const convertUnityScaleToBabylon = (vector: Vector3) => {
  return new Vector3(vector.x, vector.y, vector.z);
};

/**
 * @see https://www.gamedev.net/forums/topic/552906-closest-point-on-triangle/
 */
export function getClosestPointOnTriangle(coordinate: Vector3, triangle: readonly [Vector3, Vector3, Vector3]) {
  const edge0 = triangle[1].subtract(triangle[0]);
  const edge1 = triangle[2].subtract(triangle[0]);
  const v0 = triangle[0].subtract(coordinate);

  const a = Vector3.Dot(edge0, edge0);
  const b = Vector3.Dot(edge0, edge1);
  const c = Vector3.Dot(edge1, edge1);
  const d = Vector3.Dot(edge0, v0);
  const e = Vector3.Dot(edge1, v0);

  const det = a * c - b * b;
  let s = b * e - c * d;
  let t = b * d - a * e;

  if (s + t < det) {
    if (s < 0.0) {
      if (t < 0.0) {
        if (d < 0.0) {
          s = Scalar.Clamp(-d / a, 0.0, 1.0);
          t = 0.0;
        } else {
          s = 0.0;
          t = Scalar.Clamp(-e / c, 0.0, 1.0);
        }
      } else {
        s = 0.0;
        t = Scalar.Clamp(-e / c, 0.0, 1.0);
      }
    } else if (t < 0.0) {
      s = Scalar.Clamp(-d / a, 0.0, 1.0);
      t = 0.0;
    } else {
      let invDet = 1.0 / det;
      s *= invDet;
      t *= invDet;
    }
  } else {
    if (s < 0.0) {
      let tmp0 = b + d;
      let tmp1 = c + e;
      if (tmp1 > tmp0) {
        const numerator = tmp1 - tmp0;
        const denominator = a - 2 * b + c;
        s = Scalar.Clamp(numerator / denominator, 0.0, 1.0);
        t = 1 - s;
      } else {
        t = Scalar.Clamp(-e / c, 0.0, 1.0);
        s = 0.0;
      }
    } else if (t < 0.0) {
      if (a + d > b + e) {
        const numerator = c + e - b - d;
        const denominator = a - 2 * b + c;
        s = Scalar.Clamp(numerator / denominator, 0.0, 1.0);
        t = 1 - s;
      } else {
        s = Scalar.Clamp(-e / c, 0.0, 1.0);
        t = 0.0;
      }
    } else {
      const numerator = c + e - b - d;
      const denominator = a - 2 * b + c;
      s = Scalar.Clamp(numerator / denominator, 0.0, 1.0);
      t = 1 - s;
    }
  }

  return triangle[0].add(edge0.scale(s)).add(edge1.scale(t));
}

export const mergeMeshes = (
  world: World,
  entity: Entity,
  parent: TransformNode,
  name: string | undefined,
  meshes: Mesh[]
) => {
  if (meshes.length <= 1) return;

  let resultMeshes: Mesh[] = [];

  const isPickable = !!meshes.find((m) => m.isPickable);

  const materialNames = new Set(
    meshes.map((m) => {
      if (m.material && m.material.name) return m.material.name;
      return [];
    })
  );

  for (const materialName of materialNames) {
    const sameNameMeshes = meshes.filter((m) => m.material && m.material.name === materialName);
    if (sameNameMeshes.length > 1) {
      var mesh = Mesh.MergeMeshes(sameNameMeshes, true, true, undefined, false, false);
      if (mesh) {
        resultMeshes.push(mesh);

        for (let i = 1; i < sameNameMeshes.length; i++) {
          sameNameMeshes[i]?.material?.dispose(false, false, true);
        }
      }
    } else {
      resultMeshes.push(...sameNameMeshes);
    }
  }

  var mesh = Mesh.MergeMeshes(resultMeshes, true, true, undefined, false, true);
  if (mesh) {
    mesh.setParent(parent);
    mesh.name = name ?? parent.name ?? 'mergedMesh';

    mesh.alwaysSelectAsActiveMesh = false;
    mesh.isPickable = isPickable;

    mesh.material?.forceCompilation(mesh);

    if (entity) {
      world.addComponent(entity, 'MergedMeshes', true);
    }
    return mesh;
  }

  return undefined;
};

export const waitForMiliseconds = (milliseconds: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
};

export enum POINT_POSITION {
  OUTSIDE = -1,
  ON_SEGMENT = 0,
  INSIDE = 1,
}

/**
 * Define a function to check if a point is inside, outside or on the boundary of a polygon
 * @see https://rosettacode.org/wiki/Ray-casting_algorithm
 */
export function getPointPositionForPolygon(point: Vector2, polygon: Vector2[], inclusive = false) {
  // Initialize the number of intersections to zero
  let n = 0;

  // Loop through each edge of the polygon
  for (let i = 0; i < polygon.length; i++) {
    let j = (i + 1) % polygon.length;
    const pi = polygon[i];
    const pj = polygon[j];
    
    if (!pi || !pj) continue;

    // Check if the point is on the edge
    if (inclusive && isPointOnSegment(point, pi, pj)) return POINT_POSITION.ON_SEGMENT;

    // Check if the ray intersects the edge
    if (isIntersecting(point, pi, pj)) n++;
  }

  // Return 1 if the number of intersections is odd, -1 if even
  return n % 2 === 1 ? POINT_POSITION.INSIDE : POINT_POSITION.OUTSIDE;
}

// Define a function to check if a point is on a segment
function isPointOnSegment(p: Vector2, p1: Vector2, p2: Vector2): boolean {
  return (
    p.x >= Math.min(p1.x, p2.x) &&
    p.x <= Math.max(p1.x, p2.x) &&
    p.y >= Math.min(p1.y, p2.y) &&
    p.y <= Math.max(p1.y, p2.y)
  );
}

// Define a function to check if a ray intersects a segment
function isIntersecting(p: Vector2, p1: Vector2, p2: Vector2): boolean {
  // Check if the segment is horizontal
  if (p1.y === p2.y) return false;

  // Check if the ray is above or below the segment
  if (p.y < Math.min(p1.y, p2.y) || p.y >= Math.max(p1.y, p2.y)) return false;

  // Check if the ray is to the right of the segment
  if (p.x >= Math.max(p1.x, p2.x)) return false;

  // Check if the ray crosses the segment
  let x = ((p.y - p1.y) * (p2.x - p1.x)) / (p2.y - p1.y) + p1.x;
  return x > p.x;
}

let apiEndpoint = '/';
let assetsEndpoint = '/';
export const setAPIEndpoint = (apiPath: string, assetsPath: string) => {
  if (!apiPath.endsWith('/')) apiPath += '/';
  if (!assetsPath.endsWith('/')) assetsPath += '/';
  apiEndpoint = apiPath;
  assetsEndpoint = assetsPath;
};

export const buildSrcUrl = (src: string, isResourceServer: boolean = false) => {
  const scheme = /^([a-z][\w\-]{2,}):\/\//.exec(src)?.[1];
  const isAbsolute = scheme || /^\//.test(src);
  return isAbsolute ? src : `${isResourceServer ? assetsEndpoint : apiEndpoint}${src}`;
};

export const clamp = (value: number, min: number, max: number) => {
  return Scalar.Clamp(value, min, max);
};

export const toSigned180ScaleDegree = (degree: number) => {
  let angle = Math.abs(degree) % 360;
  if (angle > 180) angle = -(360 - angle);
  return degree < 0 ? -angle : angle;
};

export const getXZCoordinateHash = (coordinate: Vector3) => {
  return `${Math.round(coordinate.x * 100)}${Math.round(coordinate.z * 100)}`;
};

export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
